import React, { useEffect, useId, useRef } from 'react';
import { XIcon } from 'lucide-react';
type DialogProps = {
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: 'md' | 'lg' | 'xl' | 'full';
};
const widths = {
  md: 'max-w-lg',
  lg: 'max-w-3xl',
  xl: 'max-w-5xl',
  full: 'max-w-[1200px]'
};

/** Accessible modal dialog: focus moves in, Escape and the close button dismiss it. */
export function Dialog({
  title,
  subtitle,
  onClose,
  children,
  footer,
  size = 'md'
}: DialogProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  // Keep the latest onClose without re-running the focus effect on every render (which would steal input focus).
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeRef.current();
    };
    window.addEventListener('keydown', onKey);
    panelRef.current?.focus();
    return () => {
      window.removeEventListener('keydown', onKey);
      previous?.focus?.();
    };
  }, []);
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-3 sm:p-4" role="dialog" aria-modal="true" aria-labelledby={titleId} data-id="element-103" data-mp="2eozyutmqp519">
      <div ref={panelRef} tabIndex={-1} className={`flex max-h-[94vh] w-full ${widths[size]} flex-col overflow-hidden rounded-xl bg-white shadow-2xl focus:outline-none`} data-id="element-104" data-mp="2ok1n7n96y32s">
        <header className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-3.5" data-id="element-105" data-mp="2xm1146owsr1i">
          <div className="min-w-0" data-id="element-106" data-mp="3caowocyr90rg">
            <h2 id={titleId} className="text-base font-bold text-slate-900" data-id="element-107" data-mp="3c9m4m1pp4s9y">{title}</h2>
            {subtitle && <p className="text-sm text-slate-500" data-id="element-108" data-mp="37c44n0tnpne3">{subtitle}</p>}
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500" data-id="element-109" data-mp="3lbvh2x4iyvpz">
            <XIcon className="h-5 w-5" data-id="element-110" data-mp="2e05sntmpy4so" />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-5 py-4" data-id="element-111" data-mp="2iww586zbei9c">{children}</div>
        {footer && <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3" data-id="element-112" data-mp="2myr6jbh8wvn4">{footer}</footer>}
      </div>
    </div>;
}